import { z } from 'zod';

import {
    isRole,
    type Role,
    type Session,
    type SessionMembership,
    type SessionTenant,
} from '@/shared/api';

/**
 * Het identiteitsdeel van de API, zoals het over de lijn komt.
 *
 * Elk schema volgt één API Resource uit `smartkeur-api` (tussen haakjes). De
 * rest van de app ziet deze vorm nooit: `toSession` maakt er de camelCase
 * `Session` van. Velden die de API met `?->` vult zijn hier nullable.
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/**
 * Rollen komen als strings. Een rol die de app (nog) niet kent laat het
 * inloggen niet mislukken; hij geeft alleen geen toegang.
 */
const roleNames = z.array(z.string());

/**
 * (`TenantSummaryResource`) `logo_url` is een langlevende, ondertekende link, of
 * `null` als er geen bruikbaar logo is. Een media-sleutel komt nooit over de lijn.
 */
export const tenantSummarySchema = z.object({
    uuid: z.string(),
    name: z.string(),
    slug: z.string(),
    logo_url: z.string().nullable(),
    accent_color: z.string().nullable(),
});

export type TenantSummary = z.infer<typeof tenantSummarySchema>;

/** (`UserResource.membership`) het lidmaatschap in de huidige omgeving. */
export const currentMembershipSchema = z.object({
    status: z.string(),
    status_label: z.string(),
    job_title: z.string().nullable(),
    employment_type: z.string(),
    response_time_minutes: z.number().int().nullable(),
    joined_at: timestamp.nullable(),
    last_active_at: timestamp.nullable(),
});

/**
 * (`UserResource`) `roles` staat er alleen bij als de API ze heeft geladen, en
 * `tour_completed_at` alleen als je jezelf leest (sessie en `/auth/me`).
 */
export const authUserSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    avatar_url: z.string().nullable(),
    locale: z.string(),
    timezone: z.string(),
    status: z.string(),
    is_platform_admin: z.boolean(),
    last_active_at: timestamp.nullable(),
    tour_completed_at: timestamp.nullable().optional(),
    membership: currentMembershipSchema.nullable(),
    roles: roleNames.optional(),
});

export type AuthUser = z.infer<typeof authUserSchema>;

/** (`DeviceResource`) */
export const authDeviceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    platform: z.string(),
    platform_label: z.string(),
    is_field_device: z.boolean(),
    os_version: z.string().nullable(),
    app_version: z.string().nullable(),
    owner: z.object({ uuid: z.string(), name: z.string() }),
    last_seen_at: timestamp.nullable(),
    last_sync_at: timestamp.nullable(),
    trusted_until: timestamp.nullable(),
    revoked_at: timestamp.nullable(),
    is_trusted: z.boolean(),
    is_revoked: z.boolean(),
    is_current: z.boolean(),
});

export type AuthDevice = z.infer<typeof authDeviceSchema>;

/** (`MembershipResource`) */
export const authMembershipSchema = z.object({
    tenant: tenantSummarySchema,
    status: z.string(),
    status_label: z.string(),
    roles: roleNames,
    job_title: z.string().nullable(),
    employment_type: z.string(),
    response_time_minutes: z.number().int().nullable(),
    is_default: z.boolean(),
    joined_at: timestamp.nullable(),
    last_active_at: timestamp.nullable(),
});

export type AuthMembership = z.infer<typeof authMembershipSchema>;

/**
 * (`AuthenticatedSessionResource`) het antwoord van `POST /auth/login`,
 * `/auth/refresh`, `/auth/switch-tenant` en `/auth/invitations/{token}/accept`.
 */
export const authenticatedSessionSchema = z.object({
    token: z.string(),
    token_type: z.string(),
    expires_at: timestamp.nullable(),
    abilities: z.array(z.string()),
    user: authUserSchema,
    device: authDeviceSchema,
    tenant: tenantSummarySchema.nullable(),
    memberships: z.array(authMembershipSchema),
    roles: roleNames,
    permissions: z.array(z.string()),
});

export type AuthenticatedSession = z.infer<typeof authenticatedSessionSchema>;

/** (`MeResource`) het antwoord van `GET /auth/me`. */
export const meSchema = z.object({
    user: authUserSchema,
    tenant: tenantSummarySchema.nullable(),
    branding: z
        .object({
            logo_url: z.string().nullable(),
            accent_color: z.string().nullable(),
            cover_template: z.string().nullable(),
            report_footer_text: z.string().nullable(),
        })
        .nullable(),
    memberships: z.array(authMembershipSchema),
    roles: roleNames,
    permissions: z.array(z.string()),
    device: authDeviceSchema.nullable(),
    token: z.object({
        abilities: z.array(z.string()),
        expires_at: timestamp.nullable(),
    }),
});

export type Me = z.infer<typeof meSchema>;

/** (`TourResource`) het antwoord van `POST /auth/me/tour`. */
export const tourSchema = z.object({
    tour_completed_at: timestamp.nullable(),
});

/** (`InvitationPreviewResource`) `GET /auth/invitations/{token}`. */
export const invitationPreviewSchema = z.object({
    email: z.string(),
    /** De naam op de uitnodiging; na een aanmelding op de website wat de bezoeker daar typte. */
    name: z.string().nullable().optional(),
    role: z.string(),
    role_label: z.string(),
    tenant: tenantSummarySchema.nullable(),
    invited_by: z.string().nullable(),
    expires_at: timestamp,
    is_expired: z.boolean(),
    is_accepted: z.boolean(),
    requires_password: z.boolean(),
});

export interface InvitationPreview {
    email: string;
    name: string | null;
    role: Role | null;
    roleLabel: string;
    tenant: SessionTenant | null;
    invitedBy: string | null;
    expiresAt: string;
    isExpired: boolean;
    isAccepted: boolean;
    requiresPassword: boolean;
}

/* -------------------------------------------------------------------------- */
/* De ene mapping naar `Session`                                               */
/* -------------------------------------------------------------------------- */

/** Wie je bent en waar je werkt: in beide antwoorden gelijk. */
type Identity = Pick<
    AuthenticatedSession,
    'user' | 'tenant' | 'memberships' | 'roles' | 'permissions'
>;

/** Het token en het apparaat: bij `/auth/me` komen die (deels) uit de huidige sessie. */
interface Credentials {
    token: string;
    expiresAt: string | null;
    deviceUuid: string;
}

export function toSession(identity: Identity, credentials: Credentials): Session {
    return {
        token: credentials.token,
        expiresAt: credentials.expiresAt,
        device: { uuid: credentials.deviceUuid },
        user: {
            uuid: identity.user.uuid,
            name: identity.user.name,
            email: identity.user.email,
            isPlatformAdmin: identity.user.is_platform_admin,
            ...(identity.user.tour_completed_at === undefined
                ? {}
                : { tourCompletedAt: identity.user.tour_completed_at }),
        },
        tenant: identity.tenant ? toTenant(identity.tenant) : null,
        memberships: identity.memberships
            .filter((membership) => membership.status === 'active')
            .map(toMembership),
        roles: knownRoles(identity.roles),
        permissions: [...identity.permissions],
    };
}

/** Login, refresh, wisselen of een geaccepteerde uitnodiging. */
export function sessionFromAuthentication(payload: AuthenticatedSession): Session {
    return toSession(payload, {
        token: payload.token,
        expiresAt: payload.expires_at,
        deviceUuid: payload.device.uuid,
    });
}

/**
 * `GET /auth/me` bij het opstarten. Het token zelf staat niet in dat antwoord;
 * dat blijft het token waarmee gevraagd werd.
 */
export function sessionFromMe(me: Me, current: Pick<Session, 'token' | 'device'>): Session {
    return toSession(me, {
        token: current.token,
        expiresAt: me.token.expires_at,
        deviceUuid: me.device?.uuid ?? current.device.uuid,
    });
}

export function toTenant(tenant: TenantSummary): SessionTenant {
    return {
        uuid: tenant.uuid,
        name: tenant.name,
        slug: tenant.slug,
        logoUrl: tenant.logo_url,
        accentColor: tenant.accent_color,
    };
}

function toMembership(membership: AuthMembership): SessionMembership {
    return {
        tenant: toTenant(membership.tenant),
        roles: knownRoles(membership.roles),
        isDefault: membership.is_default,
    };
}

export function toInvitationPreview(
    payload: z.infer<typeof invitationPreviewSchema>,
): InvitationPreview {
    return {
        email: payload.email,
        name: payload.name ?? null,
        role: isRole(payload.role) ? payload.role : null,
        roleLabel: payload.role_label,
        tenant: payload.tenant ? toTenant(payload.tenant) : null,
        invitedBy: payload.invited_by,
        expiresAt: payload.expires_at,
        isExpired: payload.is_expired,
        isAccepted: payload.is_accepted,
        requiresPassword: payload.requires_password,
    };
}

function knownRoles(names: readonly string[]): Role[] {
    return names.filter(isRole);
}
